import { useCallback } from 'react'
import ApiClient from 'api-client/ApiClient'
import { withRetry } from 'utils/async'
import { INetworkSelector } from '../../networks/model'
import { NetworkAttachmentRow, NetworkAttachments } from '../CreateVirtualMachineModal.helpers'

export const ENABLE_SIMPLE_NETWORK_BATCH_CREATION = true

/**
 * Lookup table from an id to the network port id we created for it.
 * For simple (L2) networks the key is the network's id; for managed
 * networks the key is the attachment row's id.
 */
export type PortsMap = Record<string, string>

/** The ports we pre-create for one VM (managed-network rows only), keyed by attachment row id. */
export type NetworkPortsResult = PortsMap

/** Internal: everything needed to create one port for one managed-network row. */
interface ManagedPortWorkItem {
  network: INetworkSelector
  row: NetworkAttachmentRow
  /** Where this row sits among the auto rows for its network; used to number port names when a network has more than one. */
  rowIndex: number
  totalAutoRowsForNetwork: number
}

export const useNetworkPorts = () => {
  const getNeutron = () => ApiClient.getInstance().neutron

  const deletePort = async (portId: string): Promise<void> => {
    try {
      await getNeutron().deletePort(portId)
      console.log(`[Port] Deleted: ${portId}`)
    } catch (error) {
      console.error(`[Port] Delete failed: ${portId}`, error)
    }
  }

  const deletePorts = useCallback(async (ports: PortsMap): Promise<void> => {
    for (const portId of Object.values(ports)) {
      await deletePort(portId)
    }
  }, [])

  /**
   * FR-016: Create the network ports for a single VM's managed-network rows.
   * Ports are created together and retried on failure. We only create a port for a row
   * that has a specific subnet chosen and is set to "Automatic port". The returned map is
   * keyed by row id (not network id) so the body builder can match each row to its port.
   */
  const createManagedPortsForVM = async (
    workItems: ManagedPortWorkItem[],
    tenantId: string,
    vmName: string,
    vmIndex?: number,
    securityGroupIds: string[] = [],
  ): Promise<PortsMap> => {
    const portResults = await Promise.all(
      workItems.map(({ network, row, rowIndex, totalAutoRowsForNetwork }) =>
        withRetry(
          async () => {
            const subnet = row.subnet as { id: string; name?: string }
            const nameBase =
              vmIndex !== undefined
                ? `${vmName}-${vmIndex + 1}-${network.name}-subnet-port`
                : `${vmName}-${network.name}-subnet-port`
            const portName = totalAutoRowsForNetwork > 1 ? `${nameBase}-${rowIndex + 1}` : nameBase

            const response = await getNeutron().createPort({
              port: {
                network_id: network.id,
                fixed_ips: [{ subnet_id: subnet.id }],
                admin_state_up: true,
                tenant_id: tenantId,
                name: portName,
                // PCD-5420: bake the user's selected security groups onto the port at creation.
                // Nova ignores server-level security_groups for pre-created ports, so setting
                // them here is the only way this NIC actually gets the chosen SGs.
                ...(securityGroupIds.length ? { security_groups: securityGroupIds } : {}),
              },
            })

            const portId = response?.id
            if (!portId)
              throw new Error(
                `Port creation failed for network "${network.name}" on subnet "${
                  subnet.name ?? subnet.id
                }" (${subnet.id})`,
              )

            console.log(
              `[Managed Port] Created: ${network.name} (subnet ${
                subnet.name ?? subnet.id
              }) → ${portId}`,
            )

            return [row.rowId, portId] as const
          },
          2,
          5000,
        ),
      ),
    )

    return Object.fromEntries(portResults)
  }

  /**
   * Go through the managed networks and pick the rows that need a port created.
   * A row qualifies when it has a subnet chosen (subnet !== '') and is set to
   * "Automatic port" (port === ''). Simple networks are skipped here.
   * FR-016, FR-019: one port per qualifying row, per VM.
   */
  const buildManagedWorkItems = (
    networks: INetworkSelector[],
    networkAttachments: NetworkAttachments,
  ): ManagedPortWorkItem[] => {
    const items: ManagedPortWorkItem[] = []
    for (const network of networks) {
      if (network.isSimpleNetwork) continue
      const rows = networkAttachments[network.id] ?? []
      const autoRows = rows.filter((row) => row.subnet !== '' && row.port === '')
      const totalAutoRowsForNetwork = autoRows.length
      autoRows.forEach((row, rowIndex) => {
        items.push({ network, row, rowIndex, totalAutoRowsForNetwork })
      })
    }
    return items
  }

  /**
   * Pre-create the Neutron ports needed before the VM(s) are launched, retrying on failure.
   * Only managed-network rows that have a specific subnet chosen need a port made up front
   * (so it can be pinned to that subnet and carry the chosen security groups). Simple (L2)
   * networks are NOT pre-created — Nova creates and owns their ports from `{ uuid }`. [FR-025]
   *
   * If anything fails, every port already created in this call is deleted so we don't leave
   * orphaned ports behind.
   *
   * @param networks Every network the user picked
   * @param networkAttachments FR-014 attachment rows per managed network — a row with a chosen
   *   subnet and "Automatic port" gets a Neutron port created up front (fixed_ips: [{ subnet_id }]),
   *   keyed by its row id
   * @param tenantId OpenStack tenant/project id the ports belong to
   * @param vmName VM name, used to build readable port names
   * @param vmCount How many VMs we're creating ports for (default 1)
   * @returns A single ports map when vmCount===1, or one per VM when vmCount>1
   */
  const preCreatePorts = useCallback(
    async (
      networks: INetworkSelector[],
      networkAttachments: NetworkAttachments = {},
      tenantId: string,
      vmName: string,
      vmCount: number = 1,
      // PCD-5420: SG IDs to stamp onto each pre-created managed port (the user's selection
      // when security groups are enabled). Empty = create ports without explicit SGs.
      securityGroupIds: string[] = [],
    ): Promise<NetworkPortsResult | NetworkPortsResult[]> => {
      const managedWorkItems = buildManagedWorkItems(networks, networkAttachments)

      // No ports are needed for this selection — return an empty result.
      if (managedWorkItems.length === 0) {
        return vmCount === 1 ? {} : []
      }

      try {
        // Create the ports for every VM at the same time.
        const vmPortResults = await Promise.allSettled(
          Array.from({ length: vmCount }, async (_, i) => {
            const name = vmCount > 1 ? `${vmName}-${i + 1}` : vmName
            const index = vmCount > 1 ? i : undefined
            return createManagedPortsForVM(
              managedWorkItems,
              tenantId,
              name,
              index,
              securityGroupIds,
            )
          }),
        )

        // First gather the ports of every VM that succeeded across the whole batch, and only
        // then deal with any failure. We can't stop at the first failed VM, because a later VM
        // may have already finished creating its ports successfully and would otherwise be left
        // behind. allSettled keeps results in order and never short-circuits, so we see them all.
        const allCreatedPorts: NetworkPortsResult[] = vmPortResults
          .filter(
            (result): result is PromiseFulfilledResult<NetworkPortsResult> =>
              result.status === 'fulfilled',
          )
          .map((result) => result.value)
        const failure = vmPortResults.find(
          (result): result is PromiseRejectedResult => result.status === 'rejected',
        )

        // If any VM failed, delete every port we already created and re-throw the error.
        if (failure) {
          console.error('[Port] Creation failed, rolling back all created ports...', failure.reason)
          for (const ports of allCreatedPorts) {
            await deletePorts(ports)
          }
          throw failure.reason
        }

        return vmCount === 1 ? allCreatedPorts[0] : allCreatedPorts
      } catch (error) {
        console.error('[Port] Pre-creation failed:', error)
        throw error
      }
    },
    [],
  )

  const hasSimpleNetworks = useCallback(
    (networks: INetworkSelector[]) => networks.some((n) => n.isSimpleNetwork),
    [],
  )

  const shouldEnableConfigDrive = useCallback(
    (networks: INetworkSelector[], cloudInitEnabled: boolean) =>
      cloudInitEnabled &&
      networks.some(
        (n) => n.isSimpleNetwork || n.subnetDetails?.some((s: any) => s.enable_dhcp === false),
      ),
    [],
  )

  return {
    preCreatePorts,
    cleanupPreCreatedPorts: deletePorts,
    hasSimpleNetworks,
    shouldEnableConfigDrive,
    ENABLE_SIMPLE_NETWORK_BATCH_CREATION,
  }
}

export default useNetworkPorts
